export const T = {
  // ── Pepper red — the one brand accent, used sparingly ───────────────
  pepper: '#e5222a',
  pepperD: '#c01920',
  pepperBg: 'rgba(229,34,42,0.10)',
  pepperBorder: 'rgba(229,34,42,0.18)',
  // ── Monochrome shell ────────────────────────────────────────────────
  coral: '#f97316',
  cyan: '#0891b2',
  violet: '#7c3aed',
  gold: '#e5222a',        // alias → pepper red
  goldD: '#c01920',
  goldBg: 'rgba(229,34,42,0.10)',
  goldBorder: 'rgba(229,34,42,0.18)',
  green: 'var(--accent)',
  greenBg: 'var(--accent-bg)',
  greenHex: '#16a34a',
  danger: '#dc2626',
  dangerBg: 'rgba(220,38,38,0.10)',
  info: '#2563eb',        // informational blue only (not brand)
  warn: '#d97706',
  warnBg: 'rgba(217,119,6,0.12)',
  muted: 'var(--muted)',
  surface: 'var(--surface)',
  border: 'var(--border)',
  text: 'var(--text)',
  dim: 'var(--dim)',
  bg: 'var(--bg)',
  mono: 'var(--font-mono)',
  display: 'var(--font-display)',
  sans: 'var(--font-sans)',
} as const;

export const S = {
  card: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '10px 14px',
    boxShadow: 'var(--shadow-sm)',
    fontSize: 14,
    lineHeight: 1.5,
  } as React.CSSProperties,
  cardFlush: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: 'var(--shadow-sm)',
  } as React.CSSProperties,
  monoLabel: {
    fontFamily: 'var(--font-sans)',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.3,
    textTransform: 'uppercase' as const,
    color: 'var(--muted)',
  } as React.CSSProperties,
  sectionTitle: {
    fontFamily: 'var(--font-sans)',
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0,
    textTransform: 'none' as const,
    color: 'var(--text)',
    marginBottom: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  } as React.CSSProperties,
  input: {
    width: '100%',
    padding: '7px 10px',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 6,
    fontSize: 14,
    fontFamily: 'var(--font-sans)',
    color: 'var(--text)',
    outline: 'none',
    transition: 'border-color 0.12s, box-shadow 0.12s, background 0.12s',
  } as React.CSSProperties,
  th: {
    fontFamily: 'var(--font-sans)',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: 'uppercase' as const,
    color: 'var(--muted)',
    padding: '6px 10px',
    textAlign: 'left' as const,
    borderBottom: '1px solid var(--border)',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,
  td: {
    fontFamily: 'var(--font-sans)',
    fontSize: 13,
    padding: '7px 10px',
    borderBottom: '1px solid color-mix(in srgb, var(--border) 55%, transparent)',
    whiteSpace: 'nowrap' as const,
    color: 'var(--text)',
  } as React.CSSProperties,
} as const;

export const TT = {
  contentStyle: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    fontSize: 12,
    fontFamily: 'var(--font-sans)',
  },
  labelStyle: { color: 'var(--muted)' },
};

export function Rule({ color = T.gold }: { color?: string }) {
  return <div style={{ width: 8, height: 8, background: color, borderRadius: 99, flexShrink: 0 }} />;
}

export function PageTitle({ title, sub }: { title: string; sub?: string }) {
  return (
    <div>
      <div style={{ fontFamily: T.display, fontSize: 20, fontWeight: 750, color: T.text, lineHeight: 1.2 }}>
        {title}
      </div>
      {sub && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3, maxWidth: 640 }}>{sub}</div>}
    </div>
  );
}

export function KpiBlock({ label, value, sub, color = 'var(--text)', borderColor, accent, icon }: {
  label: string; value: React.ReactNode; sub?: React.ReactNode; icon?: React.ReactNode;
  color?: string; borderColor?: string; accent?: string;
}) {
  const tone = accent ?? borderColor ?? color;
  return (
    <div style={{
      ...S.card,
      padding: '12px 14px',
      borderColor: `color-mix(in srgb, ${tone} 18%, var(--border))`,
      background: 'var(--surface)',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 10,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ ...S.monoLabel, marginBottom: 8 }}>{label}</div>
        <div style={{ fontFamily: T.display, fontSize: 24, fontWeight: 800, color, lineHeight: 1.1 }}>
          {value}
        </div>
        {sub && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 5 }}>{sub}</div>}
      </div>
      {icon && (
        <div style={{
          width: 32,
          height: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 6,
          background: `color-mix(in srgb, ${tone} 12%, transparent)`,
          color: tone,
          flexShrink: 0,
        }}>
          {icon}
        </div>
      )}
    </div>
  );
}

export function Skeleton({ width = '100%', height = 12, radius = 8, style }: {
  width?: number | string
  height?: number | string
  radius?: number
  style?: React.CSSProperties
}) {
  return (
    <div
      style={{
        width,
        height,
        borderRadius: radius,
        background: 'linear-gradient(90deg, var(--raised), color-mix(in srgb, var(--raised) 70%, var(--text)), var(--raised))',
        backgroundSize: '220% 100%',
        animation: 'skeletonPulse 1.35s ease-in-out infinite',
        ...style,
      }}
    />
  )
}

export function SkeletonPage({ variant = 'default', rows = 4, showHeader = true }: {
  variant?: 'default' | 'dashboard' | 'cards' | 'form' | 'analytics'
  rows?: number
  showHeader?: boolean
}) {
  const cards = variant === 'dashboard' ? 5 : variant === 'cards' ? 6 : 3
  return (
    <div className="w-full">
      <style>{`
        @keyframes skeletonPulse {
          0% { background-position: 220% 0; }
          100% { background-position: -220% 0; }
        }
      `}</style>

      {showHeader && (
        <div style={{ marginBottom: 28 }}>
          <Skeleton width={180} height={26} />
          <Skeleton width={280} height={12} style={{ marginTop: 10 }} />
        </div>
      )}

      {(variant === 'dashboard' || variant === 'analytics') && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cards}, 1fr)`, gap: 12, marginBottom: 24 }}>
          {Array.from({ length: cards }).map((_, i) => (
            <div key={i} style={{ ...S.card, padding: '18px 18px' }}>
              <Skeleton width={80} height={11} />
              <Skeleton width={i % 2 ? 72 : 48} height={24} style={{ marginTop: 14 }} />
            </div>
          ))}
        </div>
      )}

      {variant === 'form' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {Array.from({ length: rows }).map((_, i) => (
            <div key={i} style={S.card}>
              <Skeleton width={130} height={12} style={{ marginBottom: 18 }} />
              <Skeleton height={40} style={{ marginBottom: 10 }} />
              <Skeleton width="72%" height={10} />
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: variant === 'cards' ? 'repeat(auto-fill, minmax(300px, 1fr))' : '1fr 1fr', gap: 14 }}>
          {Array.from({ length: rows }).map((_, i) => (
            <div key={i} style={S.card}>
              <Skeleton width={`${45 + (i % 3) * 12}%`} height={16} />
              <Skeleton height={10} style={{ marginTop: 16 }} />
              <Skeleton width="82%" height={10} style={{ marginTop: 9 }} />
              <Skeleton width="58%" height={10} style={{ marginTop: 9 }} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function DirBadge({ dir }: { dir: string }) {
  const isL = dir === 'LONG' || dir === 'long';
  return (
    <span style={{
      fontFamily: T.sans, fontSize: 12, fontWeight: 600,
      color: isL ? T.gold : T.danger,
      background: isL ? T.goldBg : T.dangerBg,
      padding: '3px 8px', borderRadius: 999,
    }}>
      {isL ? 'Long' : 'Short'}
    </span>
  );
}

export const STATUS_CFG: Record<string, { color: string; label: string }> = {
  PENDING: { color: T.warn, label: 'Pending' },
  TRIGGERED: { color: T.info, label: 'Active' },
  TP1_HIT: { color: T.warn, label: 'First target' },
  TP2_HIT: { color: T.green, label: 'Won' },
  SL_HIT: { color: T.danger, label: 'Stopped' },
  INVALIDATED: { color: 'var(--muted)', label: 'Invalid' },
  EXPIRED: { color: 'var(--muted)', label: 'Expired' },
};

export function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CFG[status];
  if (!cfg) return null;
  const isActive = status === 'PENDING' || status === 'TRIGGERED';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {isActive && (
        <span style={{
          width: 7, height: 7, borderRadius: '50%',
          background: cfg.color, display: 'inline-block', flexShrink: 0,
          animation: 'blink 2s ease-in-out infinite',
        }} />
      )}
      <span style={{
        fontFamily: T.sans, fontSize: 12, fontWeight: 600,
        color: cfg.color,
        background: `color-mix(in srgb, ${cfg.color} 12%, transparent)`,
        padding: '3px 8px', borderRadius: 999,
      }}>
        {cfg.label}
      </span>
    </span>
  );
}

export function SectionHead({ children, action }: { children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
      <span style={{ fontFamily: T.display, fontSize: 16, fontWeight: 750, color: 'var(--text)' }}>
        {children}
      </span>
      <div style={{ flex: 1 }} />
      {action}
    </div>
  );
}